import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Status kunci langganan berubah di sisi server (mis. founder mengaktifkan akun saat tab sekolah
  // masih terbuka). Saat tab kembali terlihat, ambil ulang profil tanpa mengeluarkan sesi bila gagal.
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch {
        // biarkan profil lama; penanganan sesi kedaluwarsa sudah lewat event sist:session-expired
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  useEffect(() => {
    const onExpired = () => {
      qc.clear();
      setMe(null);
      toast.error("Sesi Anda telah berakhir. Silakan masuk kembali.", { id: "session-expired" });
    };
    window.addEventListener("sist:session-expired", onExpired);
    return () => window.removeEventListener("sist:session-expired", onExpired);
  }, [qc]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    qc.clear();
    setMe(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      qc.clear();
      setMe(null);
    }
  };

  const switchSchool = async (schoolId) => {
    const { data } = await api.post("/auth/switch-school", { school_id: schoolId });
    qc.clear();
    setMe(data);
  };

  return (
    <AuthContext.Provider value={{ me, loading, login, logout, switchSchool, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const ADMIN_ROLES = ["school_admin", "kepala_sekolah", "operator"];

export function useSpaceName() {
  const { me } = useAuth();
  return ADMIN_ROLES.includes(me?.active_school?.role) ? "Dasbor Admin Sekolah" : "Ruang Sekolah";
}

const WRITE_PERMS = ["profile:update", "members:create", "members:update", "invitations:create", "invitations:revoke",
  "feed:interact", "feed:publish", "feed:moderate", "feed:settings"];

// UI hint only; the backend enforces the same rules.
export function useSchoolCan(perm) {
  const { me } = useAuth();
  const a = me?.active_school;
  if (!me?.school_permissions?.includes(perm)) return false;
  if (WRITE_PERMS.includes(perm)) {
    const sub = me.user.subscription_status;
    return a.status === "active" && a.school_status === "Active" && sub !== "Expired" && sub !== "Suspended";
  }
  return true;
}
